import { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import ScheduleBookingForm from "../components/landing/ScheduleBookingForm";
import LandingNavbar from "../components/landing/LandingNavbar";
import SiteOperatorLine from "../components/seo/SiteOperatorLine";
import usePageSeo from "../hooks/usePageSeo";
import JsonLd from "../components/seo/JsonLd";
import { buildOrganizationSchema, ORG_ID } from "../data/seo/structuredData";
import { contactPhone, marketingOrigin } from "../data/seo/siteEntity";

export default function ContactUs() {
    const phone = contactPhone();
    const description =
        import.meta.env.VITE_SITE_REGION === "in"
            ? `Get in touch with the FloGPT team. Book a 20-minute consultation, email support@flogpt.com, or call ${phone.display} for AI chat widget, voice agent, and workflow automation help.`
            : "Get in touch with the FloGPT team. Book a 20-minute consultation, email support@flogpt.com, or call +91 90078 55673 for AI chat widget, voice agent, and workflow automation help.";
    usePageSeo({
        title: "Contact FloGPT — Talk to Sales or Support for AI Chat & Voice Agents",
        description,
        keywords: "contact FloGPT, AI chat support, AI voice agent demo, FloGPT sales, talk to FloGPT",
        canonical: "/contact",
    });

    // ContactPage schema is the canonical wrapper for a contact page; it
    // links back to the Organization (single source of truth) and surfaces
    // the ContactPoints (email, phone, hours) so search and AI engines can
    // quote them directly.
    const contactPageSchema = useMemo(
        () => ({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: "Contact FloGPT",
            description:
                "Reach the FloGPT team for sales, partnerships, or support. Email, phone, and a self-service booking form for a 20-minute demo.",
            url: `${marketingOrigin()}/contact`,
            inLanguage: "en",
            mainEntity: { "@id": ORG_ID },
        }),
        [],
    );

    const organizationSchema = useMemo(() => buildOrganizationSchema(), []);

    return (
        <div className="bg-slate-900 min-h-screen">
            <LandingNavbar />

            {/* Main Content */}
            <div className="relative pt-32 pb-20 min-h-screen">
                <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mx-auto max-w-2xl text-center">
                        <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-800/40 px-4 py-2 text-sm font-medium text-slate-300 mb-8">
                            <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-orange-500 to-pink-500" />
                            Get in Touch
                        </div>

                        <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-white mb-6">
                            Talk to FloGPT — Sales, Demos &amp; Support
                        </h1>
                        <p className="text-lg text-slate-400 mb-12 leading-relaxed">
                            Book a 20-minute consultation to see AI chat and voice agents running on your own content, or reach the team by email or phone.
                        </p>
                    </motion.div>

                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="mx-auto max-w-3xl">
                        {/* Schedule Booking Form */}
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }} className="mb-12">
                            <ScheduleBookingForm />
                        </motion.div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {/* Email Card */}
                            <div className="relative group">
                                <div className="absolute -inset-0.5 bg-gradient-to-r from-orange-500 to-pink-500 rounded-2xl blur opacity-20 group-hover:opacity-30 transition duration-300" />
                                <div className="relative bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8">
                                    <div className="flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 mb-6">
                                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <h3 className="text-xl font-semibold text-white mb-3">Email Us</h3>
                                    <p className="text-slate-400 mb-4">Send us an email and we'll get back to you as soon as possible.</p>
                                    <a href="mailto:support@flogpt.com" className="text-orange-400 hover:text-orange-300 font-medium text-lg transition-colors flex items-center gap-2">
                                        support@flogpt.com
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </a>
                                </div>
                            </div>

                            {/* Phone Card */}
                            <div className="relative group">
                                <div className="absolute -inset-0.5 bg-gradient-to-r from-orange-500 to-pink-500 rounded-2xl blur opacity-20 group-hover:opacity-30 transition duration-300" />
                                <div className="relative bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8">
                                    <div className="flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 mb-6">
                                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                            />
                                        </svg>
                                    </div>
                                    <h3 className="text-xl font-semibold text-white mb-3">Call Us</h3>
                                    <p className="text-slate-400 mb-4">Give us a call during business hours for immediate assistance.</p>
                                    <a href={`tel:${phone.tel}`} className="text-orange-400 hover:text-orange-300 font-medium text-lg transition-colors flex items-center gap-2">
                                        {phone.display}
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* Additional Information */}
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }} className="mt-12 text-center">
                            <div className="bg-slate-800/30 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8">
                                <h3 className="text-xl font-semibold text-white mb-4">Business Hours</h3>
                                <p className="text-slate-300 mb-2">Monday - Friday: 9:00 AM - 6:00 PM EST</p>
                                <p className="text-slate-300">Saturday - Sunday: Closed</p>
                            </div>
                        </motion.div>

                        {/* Back Button */}
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.6 }} className="mt-12 text-center">
                            <Link to="/" className="inline-flex items-center gap-2 text-slate-300 hover:text-orange-400 transition-colors font-medium">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                                Back to Home
                            </Link>
                        </motion.div>

                        {/* Ownership Information */}
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.7 }} className="mt-8 text-center">
                            <SiteOperatorLine className="text-sm font-medium text-slate-500" />
                            {import.meta.env.VITE_SITE_REGION === "in" ? (
                                <p className="mt-2 text-sm text-slate-500">
                                    Unit No 4, Office A, 4th Floor, EK Tower, New Town, Kolkata, West Bengal 700161, India.{" "}
                                    <a href="tel:+919560303603" className="text-slate-300 hover:text-white transition-colors">
                                        +91 9560303603
                                    </a>
                                </p>
                            ) : null}
                        </motion.div>
                    </motion.div>
                </div>
            </div>

            <JsonLd id="contact-page" data={contactPageSchema} />
            <JsonLd id="contact-organization" data={organizationSchema} />
        </div>
    );
}
